/**
 * Array and object fields.
 *
 * Both recurse back through the resolver, so an array of objects of arrays
 * renders correctly without either component knowing what it contains.
 */

import type { JsonSchema } from '@/core/types/api.types'
import { hints, isRequired, orderedKeys, schemaType } from '@/core/utils/schema.utils'
import { FieldShell, type FieldProps } from './primitives'

export interface CompositeProps extends FieldProps {
  /** Injected by SchemaForm so these components stay unaware of the resolver. */
  renderChild: (args: {
    name: string
    path: string
    schema: JsonSchema
    value: unknown
    onChange: (v: unknown) => void
    required?: boolean
  }) => React.ReactNode
  path: string
}

function blankFor(schema: JsonSchema): unknown {
  switch (schemaType(schema)) {
    case 'object':
      return {}
    case 'array':
      return []
    case 'boolean':
      return false
    case 'number':
    case 'integer':
      return undefined
    default:
      return ''
  }
}

export function ArrayField(props: CompositeProps) {
  const { schema, value, onChange, disabled, renderChild, path } = props
  const items = Array.isArray(value) ? value : []
  const itemSchema = schema.items ?? { type: 'string' }
  const ui = hints(schema)

  const update = (index: number, next: unknown) => {
    const copy = [...items]
    copy[index] = next
    onChange(copy)
  }

  return (
    <FieldShell {...props}>
      <div className="stack" style={{ gap: 7 }}>
        {items.length === 0 && (
          <p className="field-help" style={{ margin: 0 }}>
            {ui.placeholder ?? 'No entries yet.'}
          </p>
        )}

        {items.map((item, index) => (
          <div className="array-row" key={index}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {renderChild({
                name: `${props.name}[${index}]`,
                path: `${path}.${index}`,
                // The label lives on the array itself; repeating it per row is noise.
                schema: { ...itemSchema, 'x-ui': { ...hints(itemSchema), label: ' ' } },
                value: item,
                onChange: (next) => update(index, next),
              })}
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={disabled}
              aria-label={`Remove item ${index + 1}`}
              onClick={() => onChange(items.filter((_, i) => i !== index))}
            >
              ✕
            </button>
          </div>
        ))}

        <button
          type="button"
          className="btn btn-sm"
          style={{ alignSelf: 'flex-start' }}
          disabled={disabled}
          onClick={() => onChange([...items, blankFor(itemSchema)])}
        >
          + Add
        </button>
      </div>
    </FieldShell>
  )
}

export function ObjectField(props: CompositeProps) {
  const { schema, value, onChange, renderChild, path } = props
  const obj = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>
  const keys = orderedKeys(schema)

  return (
    <FieldShell {...props}>
      <div className="nested">
        {keys.map((key) => {
          const child = schema.properties![key]
          return (
            <div key={key}>
              {renderChild({
                name: `${props.name}.${key}`,
                path: `${path}.${key}`,
                schema: child,
                value: obj[key],
                onChange: (next) => {
                  const copy = { ...obj }
                  if (next === undefined) delete copy[key]
                  else copy[key] = next
                  onChange(copy)
                },
                required: isRequired(schema, key),
              })}
            </div>
          )
        })}
      </div>
    </FieldShell>
  )
}
